import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { isApiError } from '@repletoryreach/sdk-api';
import { Stack, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { PasswordRules } from '../../../../app/components/PasswordRules';
import { isPasswordValid, passwordRefusalMessage } from '../../../../lib/password';
import { useT } from '../../../../i18n/I18nProvider';

export function ResetForm({ token, email: initialEmail }: { token: string; email: string }) {
  const t = useT();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Field-level errors: these belong on the offending input, not in a shared alert. */
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** The email identifies the token's owner; editing a prefilled one only ever breaks it. */
  const emailLocked = initialEmail.length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPasswordError(null);
    setConfirmError(null);

    /*
     * The only client rule used to be `length < 8`, so `newpassword` cleared the
     * UI's bar and came back rejected in framework English after a round trip
     * (auth-reset-password.md §4 item 1). Now the full policy is checked here
     * and reported on the field that failed.
     */
    if (!isPasswordValid(password)) {
      setPasswordError(t('auth.reset.form.passwordHint'));
      return;
    }
    if (password !== confirm) {
      setConfirmError(t('auth.reset.errors.mismatch'));
      return;
    }
    if (!token) { setError(t('auth.reset.errors.invalidLink')); return; }

    setBusy(true);
    try {
      await resetPassword(email, token, password);
      notifySuccess(t('auth.reset.toast.done'), t('auth.reset.toast.doneTitle'));
      navigate('/auth/login', { replace: true });
    } catch (err) {
      // A mismatched email reports as "invalid or expired reset link", which
      // blames the link rather than the edited field.
      const refused = passwordRefusalMessage(err, t);
      if (refused) {
        setError(refused);
      } else if (isApiError(err) && !emailLocked && email !== initialEmail) {
        setError(t('auth.reset.errors.emailMismatch'));
      } else {
        setError(t('auth.reset.errors.invalidLink'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label={t('common.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
          readOnly={emailLocked}
          autoComplete="email"
        />
        <Stack gap={4}>
          <PasswordInput
            label={t('auth.reset.form.newPassword')}
            description={t('auth.reset.form.passwordHint')}
            error={passwordError}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            required
            autoComplete="new-password"
            autoFocus
          />
          {password.length > 0 && <PasswordRules value={password} />}
        </Stack>
        <PasswordInput
          label={t('auth.reset.form.confirmPassword')}
          error={confirmError}
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
          required
          autoComplete="new-password"
        />
        {error && (
          <Alert color="error" role="alert">
            {error}{' '}
            {/* A dead link used to be a dead end: no route to a fresh one. */}
            <Link to="/auth/forgot-password">{t('auth.reset.errors.requestNew')}</Link>
          </Alert>
        )}
        <Button type="submit" loading={busy} fullWidth>{t('auth.reset.submit')}</Button>
      </Stack>
    </form>
  );
}
